import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react'

/**
 * The channel that lets a page put its own title in the app bar.
 *
 * ── Why a context and not a lookup ──
 * The bar is rendered by `AppShell`, above the router outlet, so it has no idea which page is
 * mounted or what that page calls itself. The obvious shortcut is a route-to-title map in the
 * shell, and it is a trap: the title would then live in two places — the map and the page's own
 * `PageHeader` — and the two drift the moment anyone renames a page. `/earn` reading "Deposit"
 * is exactly the kind of thing that survives review.
 *
 * The page stays the single source of truth. It passes its title to `PageHeader` as it always
 * did; `PageHeader` publishes that upward, and the bar renders whatever it is told.
 *
 * ── Why the setter is memoised and the value is not an object literal ──
 * `PageHeader` publishes from an effect keyed on title and icon. If the context value were a
 * fresh object each render, every page render would notify every consumer and the bar would
 * re-render on any state change anywhere below it — including the per-second ticks on pages
 * that stream a figure.
 */
export interface PageMeta {
  icon: ComponentType<{ className?: string }>
  title: string
}

const PageMetaValue = createContext<PageMeta | null>(null)
const PageMetaSetter = createContext<(meta: PageMeta | null) => void>(() => {})

export function PageMetaProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<PageMeta | null>(null)

  return (
    <PageMetaSetter.Provider value={setMeta}>
      <PageMetaValue.Provider value={meta}>{children}</PageMetaValue.Provider>
    </PageMetaSetter.Provider>
  )
}

/** What the app bar should currently be showing. `null` on a route with no page header. */
export function usePageMeta() {
  return useContext(PageMetaValue)
}

/**
 * Publish this page's identity to the bar.
 *
 * Clears on unmount, which matters more than it sounds: without it, navigating to a route whose
 * page has no header would leave the PREVIOUS page's title sitting in the bar, and the bar is
 * the one element on screen claiming to say where you are.
 */
export function usePublishPageMeta(meta: PageMeta | null) {
  const setMeta = useContext(PageMetaSetter)
  const { icon, title } = meta ?? {}

  // Keyed on the fields rather than the object, so a page that rebuilds its props object every
  // render does not republish on every render.
  const stable = useMemo(
    () => (icon && title ? { icon, title } : null),
    [icon, title],
  )

  useEffect(() => {
    setMeta(stable)
    return () => setMeta(null)
  }, [setMeta, stable])
}
